import {
	ICampaignDefinition,
	ICampaignState,
	ICountedActivityEvent,
	ICountedActivitySequenceEvent,
	IFloodgateStringProvider,
	ISurvey,
	ISurveyEvent,
	ISurveyMetadata,
	LAUNCHER_TYPE_DEFAULT as SURVEY_LAUNCHER_TYPE_DEFAULT,
	SurveyInfoLaunchType,
} from "@ms-ofb/floodgate-shared-typings";
import {
	CountedActivityEvent,
	CountedActivityEventData,
	CountedActivitySequenceEvent,
	CountedActivitySequenceEventData,
} from "../SurveyEvents";
import { Metadata, SurveyMetadata } from "../SurveyMetadata";
import DialogSurvey from "../Surveys/DialogSurvey";
import FpsSurvey from "../Surveys/FpsSurvey";
import GenericMessagingSurfaceSurvey from "../Surveys/GenericMessagingSurfaceSurvey";
import InterceptSurvey from "../Surveys/InterceptSurvey";
import NlqsSurvey from "../Surveys/NlqsSurvey";
import NpsSurvey from "../Surveys/NpsSurvey";
import SurveyDataSource from "../Surveys/SurveyDataSource";
import * as Utils from "../Utils";
import {
	CampaignSurveyEvent,
	CampaignSurveyEventCountedActivity,
	CampaignSurveyEventCountedActivitySequence,
	CampaignSurveyMetadata,
	CampaignSurveyTemplateDialog,
	CampaignSurveyTemplateFps,
	CampaignSurveyTemplateGenericMessagingSurface,
	CampaignSurveyTemplateIntercept,
	CampaignSurveyTemplateNlqs,
	CampaignSurveyTemplateNps,
	CampaignSurveyTemplateNps11PointStatic,
	CampaignSurveyTemplateNps5PointStatic,
} from "./CampaignDefinitionProvider";

const { dateToShortUtcString, isNOU } = Utils;

/**
 * Contains methods to take a campaign definition + state and generate an ISurvey
 */
export class CampaignSurveyFactory {
	public static makeSurvey(state: ICampaignState, definition: ICampaignDefinition, stringProvider: IFloodgateStringProvider): ISurvey {
		if (!state || !definition || !definition.surveyTemplate || !stringProvider) {
			return null;
		}

		const surveyDataSource = CampaignSurveyFactory.makeSurveyDataSourceData(state, definition);
		const surveyModel = definition.surveyTemplate;
		if (surveyModel instanceof CampaignSurveyTemplateNps5PointStatic) {
			return NpsSurvey.make5Point(surveyDataSource, stringProvider);
		} else if (surveyModel instanceof CampaignSurveyTemplateNps11PointStatic) {
			return NpsSurvey.make11Point(surveyDataSource, stringProvider);
		} else if (surveyModel instanceof CampaignSurveyTemplateFps) {
			return FpsSurvey.makeFps(surveyDataSource, stringProvider, surveyModel);
		} else if (surveyModel instanceof CampaignSurveyTemplateNlqs) {
			return NlqsSurvey.makeNlqs(surveyDataSource, stringProvider, surveyModel);
		} else if (surveyModel instanceof CampaignSurveyTemplateNps) {
			return NpsSurvey.makeCustom(surveyDataSource, stringProvider, surveyModel);
		} else if (surveyModel instanceof CampaignSurveyTemplateGenericMessagingSurface) {
			return GenericMessagingSurfaceSurvey.makeCustom(surveyDataSource);
		} else if (surveyModel instanceof CampaignSurveyTemplateIntercept) {
			return InterceptSurvey.makeIntercept(surveyDataSource, stringProvider, surveyModel);
		} else if (surveyModel instanceof CampaignSurveyTemplateDialog){
			return DialogSurvey.makeCustom(surveyDataSource);
		} else {
			return null;
		}
	}

	private static makeSurveyDataSourceData(state: ICampaignState, definition: ICampaignDefinition): SurveyDataSource.SurveyDataSourceData {
		if (!state || !definition || !definition.surveyTemplate) {
			return null;
		}

		const surveyModel = definition.surveyTemplate;
		const surveyData = new SurveyDataSource.SurveyDataSourceData();
		surveyData.activationEvent = CampaignSurveyFactory.makeEvent(state, surveyModel.activationEvent as CampaignSurveyEvent);
		surveyData.metadata = CampaignSurveyFactory.makeMetadata(surveyModel.metadata);
		surveyData.id = state.LastSurveyId;
		surveyData.backEndIdentifier = state.CampaignId;
		surveyData.expirationTimeUtc = dateToShortUtcString(state.LastSurveyExpirationTimeUtc);
		surveyData.governedChannelType = definition.governedChannelType;
		surveyData.startTimeUtc = dateToShortUtcString(state.LastSurveyStartTimeUtc);
		surveyData.additionalDataRequested = definition.additionalDataRequested || [];
		surveyData.launcherType = definition.launcherType || SURVEY_LAUNCHER_TYPE_DEFAULT;
		surveyData.preferredLaunchType = definition.surveyTemplate.preferredLaunchType || SurveyInfoLaunchType.Default;
		surveyData.businessLevel = definition.businessLevel;
		surveyData.tags = definition.tags;
		surveyData.treatmentType = definition.treatmentType;

		if (!isNOU(surveyModel.maxLaunches)) {
			surveyData.maxLaunches = surveyModel.maxLaunches;
		}

		if (!isNOU(surveyModel.maxLaunchesPerSession)) {
			surveyData.maxLaunchesPerSession = surveyModel.maxLaunchesPerSession;
		}

		return surveyData;
	}

	private static makeCountedActivityEventData(eventModel: CampaignSurveyEventCountedActivity): CountedActivityEventData {
		if (!eventModel) {
			return null;
		}

		const eventData = new CountedActivityEventData();
		eventData.activity = eventModel.activity;
		eventData.count = eventModel.count;
		eventData.isAggregate = eventModel.isAggregate;

		return eventData;
	}

	private static makeCountedActivityEvent(state: ICampaignState, eventModel: CampaignSurveyEventCountedActivity): ICountedActivityEvent {
		if (!state || !eventModel) {
			return null;
		}

		const eventData: CountedActivityEventData = CampaignSurveyFactory.makeCountedActivityEventData(eventModel);
		if (!eventData) {
			return null;
		}

		return new CountedActivityEvent(eventData);
	}

	private static makeCountedActivitySequenceEvent(state: ICampaignState,
		eventModel: CampaignSurveyEventCountedActivitySequence): ICountedActivitySequenceEvent {

		if (!state || !eventModel) {
			return null;
		}

		const sequenceEventData = new CountedActivitySequenceEventData();
		sequenceEventData.sequence = [];

		for (const event of eventModel.sequence) {
			const eventData: CountedActivityEventData = CampaignSurveyFactory.makeCountedActivityEventData(event);
			if (!eventData) {
				return null;
			}

			sequenceEventData.sequence.push(eventData);
		}

		return new CountedActivitySequenceEvent(sequenceEventData);
	}

	private static makeEvent(state: ICampaignState, eventModel: CampaignSurveyEvent): ISurveyEvent {
		if (eventModel instanceof CampaignSurveyEventCountedActivity) {
			return CampaignSurveyFactory.makeCountedActivityEvent(state, eventModel as CampaignSurveyEventCountedActivity);
		} else if (eventModel instanceof CampaignSurveyEventCountedActivitySequence) {
			return CampaignSurveyFactory.makeCountedActivitySequenceEvent(state, eventModel as CampaignSurveyEventCountedActivitySequence);
		} else {
			return null;
		}
	}

	private static makeMetadata(metadataModel: CampaignSurveyMetadata): ISurveyMetadata {
		if (isNOU(metadataModel) || isNOU(metadataModel.contentMetadata)) {
			return null;
		}

		const metadata = new Metadata();
		metadata.contentMetadata = metadataModel.contentMetadata;

		return new SurveyMetadata(metadata);
	}
}
